'use client'

import { useEffect } from 'react'
import { Controller, FieldError, useFormContext } from 'react-hook-form'
import { Cell, InfoBanner, InputText, Label, Spacer } from '@vinted/web-ui'
import { omit } from 'lodash'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { InputBirthdate } from '@marketplace-web/common-components/input-birthdate-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { SelectableInputDropdown } from '@marketplace-web/common-components/selectable-input-dropdown-ui'
import { PepType } from '@marketplace-web/domain/politically-exposed-person-data'
import { renderValidation } from '@marketplace-web/vendor-abstractions/react-hook-form-ui'
import { isDateInTheFuture } from '@marketplace-web/shared/utils'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { Screen } from 'constants/tracking/screens'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { clickEvent, viewScreenEvent } from '_libs/common/event-tracker/events'

import { Field } from '../../constants'
import { FormModel } from '../../types'

type Props = {
  screenName: Screen
  isLoading?: boolean
}

const PepDetails = ({ screenName, isLoading }: Props) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const pepTranslate = useTranslate('wallet.activation.pep')
  const breakpoints = useBreakpoint()

  const {
    register,
    control,
    watch,
    formState: { errors },
    resetField,
  } = useFormContext<FormModel>()

  const getValidationError = (fieldTitleKey: string, error: FieldError | undefined) => {
    const getMessage = () =>
      error ? pepTranslate(`fields.${fieldTitleKey}.errors.validate`) : null

    return renderValidation(getMessage())
  }

  const renderDateValidation = (fieldTitleKey: string, error: FieldError | undefined) => {
    const validationErrorType = 'validate'

    if (error?.type === validationErrorType && error?.message)
      return renderValidation(error.message)

    return getValidationError(fieldTitleKey, error)
  }

  const dateValidationRules = {
    required: translate('birthdate.validation.invalid'),
    validate: date => !isDateInTheFuture(date) || translate('birthdate.validation.invalid'),
  }

  const pepType = watch(Field.PepType)

  useEffect(() => {
    resetField(Field.PepFullName)
    resetField(Field.PepPosition)
    resetField(Field.PepOrganisation)
    resetField(Field.PepPositionDate)
  }, [resetField, pepType])

  useEffect(() => {
    track(viewScreenEvent({ screen: Screen.PepStatusSection }))
  }, [track])

  useEffect(() => {
    if (pepType === PepType.Pep) {
      track(viewScreenEvent({ screen: Screen.PepDetails }))
    }

    if (pepType === PepType.PepAssociate) {
      track(viewScreenEvent({ screen: Screen.PepFamilyDetails }))
    }
  }, [pepType, track])

  const recordDropdownOpenEvent = () => {
    track(
      clickEvent({
        target: ClickableElement.SelectStatus,
        screen: screenName,
      }),
    )
  }

  function handlePepStatusChange(onChange: (...event: Array<unknown>) => void) {
    return (
      selectedValue:
        | { id: string; title: string; eventTarget: ClickableElement; body?: string }
        | null
        | undefined,
    ) => {
      if (!selectedValue) return

      track(
        clickEvent({
          target: selectedValue.eventTarget,
          screen: Screen.PepSelection,
        }),
      )

      onChange(selectedValue.id)
    }
  }

  function renderLabel(text: string) {
    return <Label styling={Label.Styling.Wide} type={Label.Type.Leading} text={text} />
  }

  function renderPepTypeSelect() {
    const pepStatuses: Array<{
      id: PepType
      title: string
      eventTarget: ClickableElement
      body?: string
    }> = [
      {
        id: PepType.NotPep,
        eventTarget: ClickableElement.NotPep,
        title: pepTranslate('fields.pep_status.statuses.i_am_not_a_pep.title'),
      },
      {
        id: PepType.Pep,
        eventTarget: ClickableElement.Pep,
        title: pepTranslate('fields.pep_status.statuses.i_am_a_pep.title'),
      },
      {
        id: PepType.PepAssociate,
        eventTarget: ClickableElement.PepRelative,
        title: pepTranslate('fields.pep_status.statuses.family_member_associate_of_a_pep.title'),
      },
    ]

    const selectedPepStatus = pepStatuses.find(status => status.id === pepType)

    return (
      <Controller
        control={control}
        name={Field.PepType}
        rules={{
          required: true,
        }}
        render={({ field: { onChange } }) => (
          <SelectableInputDropdown
            isPhone={!!breakpoints.phones}
            name={Field.PepType}
            inputTitle={pepTranslate('fields.pep_status.title')}
            placeholder={pepTranslate('fields.pep_status.placeholder')}
            textSave={translate('actions.save')}
            data={pepStatuses}
            onSave={handlePepStatusChange(onChange)}
            onOpen={recordDropdownOpenEvent}
            selectedValue={selectedPepStatus}
            validation={getValidationError('pep_status', errors[Field.PepType])}
            disabled={isLoading}
          />
        )}
      />
    )
  }

  function renderPepFields() {
    return (
      <>
        {renderLabel(pepTranslate('i_am_a_pep.title'))}
        <InputText
          {...register(Field.PepPosition, {
            required: true,
          })}
          styling={InputText.Styling.Wide}
          title={pepTranslate('fields.position.title')}
          placeholder={pepTranslate('fields.position.placeholder')}
          validation={getValidationError('position', errors[Field.PepPosition])}
          disabled={isLoading}
        />
        <InputText
          {...register(Field.PepOrganisation, {
            required: true,
          })}
          styling={InputText.Styling.Wide}
          title={pepTranslate('fields.organisation.title')}
          placeholder={pepTranslate('fields.organisation.placeholder')}
          validation={getValidationError('organisation', errors[Field.PepOrganisation])}
          disabled={isLoading}
        />
        <Controller
          render={({ field }) => {
            const fieldPropsWithoutRef = omit(field, 'ref')

            return (
              <InputBirthdate
                {...fieldPropsWithoutRef}
                isPhone={!!breakpoints.phones}
                name={Field.PepPositionDate}
                styling={InputText.Styling.Wide}
                title={pepTranslate('fields.position_date.title')}
                translate={translate}
                validation={renderDateValidation('position_date', errors[Field.PepPositionDate])}
                disabled={isLoading}
              />
            )
          }}
          name={Field.PepPositionDate}
          control={control}
          rules={dateValidationRules}
        />
      </>
    )
  }

  function renderPepAssociateFields() {
    return (
      <>
        {renderLabel(pepTranslate('family_member_associate_of_a_pep.title'))}
        <InputText
          {...register(Field.PepFullName, {
            required: true,
          })}
          styling={InputText.Styling.Wide}
          title={pepTranslate('fields.family_or_associate_full_name.title')}
          placeholder={pepTranslate('fields.family_or_associate_full_name.placeholder')}
          validation={
            getValidationError('family_or_associate_full_name', errors[Field.PepFullName]) ||
            pepTranslate('fields.family_or_associate_full_name.note')
          }
          disabled={isLoading}
        />
        <InputText
          {...register(Field.PepPosition, {
            required: true,
          })}
          styling={InputText.Styling.Wide}
          title={pepTranslate('fields.family_or_associate_position.title')}
          placeholder={pepTranslate('fields.family_or_associate_position.placeholder')}
          validation={getValidationError('family_or_associate_position', errors[Field.PepPosition])}
          disabled={isLoading}
        />
        <InputText
          {...register(Field.PepOrganisation, {
            required: true,
          })}
          styling={InputText.Styling.Wide}
          title={pepTranslate('fields.family_or_associate_pep_organisation.title')}
          placeholder={pepTranslate('fields.family_or_associate_pep_organisation.placeholder')}
          validation={getValidationError(
            'family_or_associate_pep_organisation',
            errors[Field.PepOrganisation],
          )}
          disabled={isLoading}
        />
        <Controller
          render={({ field }) => {
            const fieldPropsWithoutRef = omit(field, 'ref')

            return (
              <InputBirthdate
                {...fieldPropsWithoutRef}
                isPhone={!!breakpoints.phones}
                translate={translate}
                name={Field.PepPositionDate}
                styling={InputText.Styling.Wide}
                title={pepTranslate('fields.family_or_associate_pep_position_date.title')}
                validation={renderDateValidation(
                  'family_or_associate_pep_position_date',
                  errors[Field.PepPositionDate],
                )}
                disabled={isLoading}
              />
            )
          }}
          name={Field.PepPositionDate}
          control={control}
          rules={dateValidationRules}
        />
      </>
    )
  }

  function renderFields() {
    if (pepType === PepType.Pep) return renderPepFields()
    if (pepType === PepType.PepAssociate) return renderPepAssociateFields()

    return undefined
  }

  return (
    <>
      {renderLabel(pepTranslate('title'))}
      <Cell styling={Cell.Styling.Tight}>
        <InfoBanner type={InfoBanner.Type.Info} body={pepTranslate('info_banner.body')} />
        <Cell title={pepTranslate('status.title')} body={pepTranslate('status.body')} />
        <SeparatedList separator={<Spacer />}>
          {renderPepTypeSelect()}
          {renderFields()}
        </SeparatedList>
      </Cell>
    </>
  )
}

export default PepDetails
